import { Alert, AlertTitle, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material";
import React, { useState } from "react"
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks"
import { toggleVobRequestNoteResolutionModal } from "../../../../../app/slices/VerificationModuleSlice";
import { VobRequestNoteResolutionCommand, VerificationSaveCommand } from "../../../../../services/verifyService/commands";
import { Note } from "../../../../../services/verifyService/model/verification/Note";
import { addNote, editAttributes } from "../../../../../services/verifyService/model/verification/VerificationSlice";
import { useResolveVobRequestNoteMutation, useSaveVerificationMutation } from "../../../../../services/verifyService/verify-api";
import { ReduxUpdate } from "../../../../../utils/ReduxUpdateCommands";
import { Verification } from "../../../../../services/verifyService/model";

function VobRequestNoteResolution(){
    const dispatch = useAppDispatch();
    const modalOpen = useAppSelector((state) => state.verificationModule.VobRequestNoteResolutionModal);
    const verification = useAppSelector((state) => state.verification.verification);
    const [resolutionNote, setResolutionNote] = useState('');
    const [saveVobRequestNoteResolutionToServer] = useResolveVobRequestNoteMutation();
    const [saveVerification] = useSaveVerificationMutation();
    const [isSaving, setIsSaving] = useState(false);
    
    const saveNote = async () => {
        setIsSaving(true);
        try {
            let newNote = new Note();
            newNote.noteText = `VOB REQUEST RESOLVED - ${resolutionNote}`; 
            newNote.changeBy = 'You'; 
            newNote.changeDate = new Date();
            dispatch(addNote(newNote));
            // Uncheck the VOB Request toggle when resolved
            dispatch(editAttributes(new ReduxUpdate("isVobRequest", false)));
            // First save the resolution event, then save verification to avoid race condition
            await saveVobRequestNoteResolutionToServer(new VobRequestNoteResolutionCommand(verification.id, resolutionNote)).unwrap();
            // Save the verification with isVobRequest set to false
            const updatedVerification = { ...verification, verifyAttributes: { ...verification.verifyAttributes, isVobRequest: false } } as Verification;
            await saveVerification(new VerificationSaveCommand(updatedVerification)).unwrap();
            setResolutionNote('');
            dispatch(toggleVobRequestNoteResolutionModal());
        } catch (error) {
            console.error('Error saving resolution:', error);
            // TODO: Add user-facing error notification (e.g., toast/snackbar)
            alert('Failed to save resolution. Please try again.');
        } finally {
            setIsSaving(false);
        }
    }

    const handleClose = () => {
        if (isSaving) return; // Prevent closing during save
        setResolutionNote('');
        dispatch(toggleVobRequestNoteResolutionModal());
    }

    return <React.Fragment>
        <Dialog 
            open={modalOpen} 
            onClose={isSaving ? undefined : handleClose} 
            maxWidth="md" 
            fullWidth
            disableEscapeKeyDown={isSaving}
        >
            <DialogTitle>Resolve VOB Request</DialogTitle>
            <DialogContent>
                <Alert severity="success" sx={{marginBottom:'20px'}}>
                    <AlertTitle>Resolution</AlertTitle>
                    Please provide comments to resolve this VOB request.
                </Alert>
                <TextField 
                    label='Resolution Comments' 
                    value={resolutionNote} 
                    onChange={(e) => setResolutionNote(e.target.value)} 
                    multiline 
                    required
                    fullWidth
                    InputLabelProps={{shrink:true}} 
                    sx={{marginTop:'10px'}} 
                    minRows='3'
                    placeholder="Enter your resolution comments..."
                    inputProps={{ maxLength: 1000 }}
                    helperText={`${resolutionNote.length}/1000 characters`}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={isSaving}>Cancel</Button>
                <Button 
                    onClick={saveNote}
                    disabled={!resolutionNote.trim() || isSaving}
                    variant="contained"
                    color="success"
                >
                    {isSaving ? 'Saving...' : 'Mark Resolved'}
                </Button>
            </DialogActions>
        </Dialog>
    </React.Fragment>
}

export { VobRequestNoteResolution }
